import { formatSEK } from '@budgie/shared';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import { CategoryTransactions } from './CategoryTransactions';

type CategoryItem = {
  id: string;
  name: string;
  groupId: string | null;
  groupName: string | null;
  total: number;
  transactionCount: number;
};

type Props = {
  year: number;
  month: number;
  categories: CategoryItem[];
  uncategorized: { total: number; count: number };
};

type Row = {
  key: string;
  label: string;
  total: number;
  transactionCount: number;
  categoryId: string | null;
};

type Group = { label: string; total: number; items: Row[] };

/**
 * Every category with spending in the period, grouped, largest first. Tap a
 * category to see (and fix) the transactions behind it.
 */
export function CategoryBreakdown({ year, month, categories, uncategorized }: Props) {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const groupMap = new Map<string, Group>();
  for (const category of categories) {
    const label = category.groupName ?? 'Övrigt';
    const row: Row = {
      key: category.id,
      label: category.name,
      total: category.total,
      transactionCount: category.transactionCount,
      categoryId: category.id,
    };
    const existing = groupMap.get(label);
    if (existing) {
      existing.total += row.total;
      existing.items.push(row);
    } else {
      groupMap.set(label, { label, total: row.total, items: [row] });
    }
  }

  const groups = [...groupMap.values()].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  for (const group of groups) group.items.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

  if (uncategorized.count > 0) {
    groups.push({
      label: 'Utan kategori',
      total: uncategorized.total,
      items: [
        {
          key: 'uncategorized',
          label: 'Okategoriserade köp',
          total: uncategorized.total,
          transactionCount: uncategorized.count,
          categoryId: null,
        },
      ],
    });
  }

  const toggle = (key: string) =>
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        När du har kategoriserade köp i perioden visas de här.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.label}>
          <header className="flex items-baseline justify-between gap-3 pb-1">
            <h3 className="text-xs font-medium text-muted-foreground">{group.label}</h3>
            <span className="text-xs text-muted-foreground tabular-nums">
              {kr(Math.abs(group.total))}
            </span>
          </header>
          <ul className="divide-y divide-border">
            {group.items.map((item) => {
              const isExpanded = expandedKeys.includes(item.key);
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => toggle(item.key)}
                    className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-lg px-2 py-3 text-left transition-colors hover:bg-muted/60 sm:gap-3"
                  >
                    <ChevronRight
                      className={cn(
                        'size-4 shrink-0 text-muted-foreground transition-transform',
                        isExpanded && 'rotate-90',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {item.label}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {item.transactionCount} st
                    </span>
                    <span className="w-22 shrink-0 text-right text-sm font-medium whitespace-nowrap tabular-nums sm:w-24">
                      {formatSEK(Math.abs(item.total))}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="pb-2 pl-4 sm:pl-7">
                      <CategoryTransactions
                        year={year}
                        month={month}
                        categoryId={item.categoryId}
                        enabled={isExpanded}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
